/**
 * Reports whether a playlist account or playlist row is read-only for the
 * signed-in customer, such as a playlist linked through a reseller LunaKey.
 * Any single read-only signal wins so management actions stay hidden.
 *
 * @param {{ readOnly?: boolean, managedBy?: string, source?: string }|null|undefined} entry
 * @returns {boolean}
 */
export const isReadOnlyPlaylistEntry = (entry) =>
  entry?.readOnly === true ||
  entry?.managedBy === "reseller" ||
  entry?.source === "reseller-managed";

/**
 * Reports whether a reseller-managed row has lost its active entitlement.
 *
 * @param {{ status?: string }|null|undefined} entry
 * @returns {boolean}
 */
export const isInactivePlaylistEntry = (entry) => entry?.status === "inactive";

export const transformPlaylistFromApi = (playlist, account = {}) => {
  const lunaKey = account.code;
  const readOnly =
    isReadOnlyPlaylistEntry(playlist) || isReadOnlyPlaylistEntry(account);
  const codeString =
    (typeof playlist.code === "string" && playlist.code) || lunaKey?.code || "";
  const addedDateValue = playlist.addedDate || playlist.createdAt;
  const addedDateParsed = addedDateValue ? new Date(addedDateValue) : null;
  const addedDate =
    addedDateParsed && !Number.isNaN(addedDateParsed.getTime())
      ? addedDateParsed.toISOString().split("T")[0]
      : null;
  const lastAccessedParsed = playlist.lastAccessed
    ? new Date(playlist.lastAccessed)
    : null;
  const lastAccessed =
    lastAccessedParsed && !Number.isNaN(lastAccessedParsed.getTime())
      ? lastAccessedParsed.toISOString().split("T")[0]
      : null;

  return {
    accountId: playlist.accountId ?? account.accountId ?? null,
    accountName: playlist.accountName || account.displayName || "",
    accountPlaylistCount:
      playlist.accountPlaylistCount ?? account.playlistCount ?? 1,
    accountSource:
      playlist.accountSource || account.source || "legacy-user-level",
    accountStatus: playlist.accountStatus || account.status || "active",
    providerAccessStatus: playlist.providerAccessStatus || "unknown",
    id: playlist.id,
    domain: playlist.domain,
    uriHost: playlist.uriHost,
    username: playlist.username,
    password: "********",
    name: playlist.fullName
      ? playlist.fullName
      : playlist.firstName
        ? [playlist.firstName, playlist.lastName].filter(Boolean).join(" ")
        : playlist.accountName || playlist.name,
    playlistName: playlist.name,
    code: codeString,
    redeemType: playlist.redeemType || lunaKey?.redeemType || "unknown",
    expiresAt: playlist.expiresAt || lunaKey?.expiresAt || null,
    autoRenew: playlist.autoRenew ?? lunaKey?.autoRenew ?? false,
    canAddPlaylistToAccount: playlist.canAddPlaylistToAccount === true,
    addedDate,
    lastAccessed,
    devicesCount: playlist.devicesCount || account.devicesCount || 0,
    isResellerPackage:
      playlist.isResellerPackage === true ||
      lunaKey?.isResellerPackage === true,
    autoDeleteEnabled:
      playlist.autoDeleteEnabled === true ||
      lunaKey?.autoDeleteEnabled === true,
    autoDeleteScheduledFor:
      playlist.autoDeleteScheduledFor ||
      lunaKey?.autoDeleteScheduledFor ||
      null,
    maxPlaylistsPerAccount:
      playlist.maxPlaylistsPerAccount ?? account.maxPlaylistsPerAccount ?? 3,
    playlistLimitReached:
      playlist.playlistLimitReached === true ||
      account.playlistLimitReached === true,
    readOnly,
    managedBy: readOnly
      ? "reseller"
      : playlist.managedBy || account.managedBy || "customer",
    status: playlist.status || account.status || "active",
  };
};

export const transformAccountGroupFromApi = (account) => {
  const playlists = (account.playlists || []).map((playlist) =>
    transformPlaylistFromApi(playlist, account)
  );
  const lunaKey = account.code;
  const codeString = lunaKey?.code || playlists[0]?.code || "";
  const playlistCount = account.playlistCount ?? playlists.length;
  const readOnly =
    isReadOnlyPlaylistEntry(account) ||
    playlists.some((playlist) => playlist.readOnly);

  return {
    ...account,
    readOnly,
    managedBy: readOnly ? "reseller" : account.managedBy || "customer",
    rowKey: String(
      account.id ||
        account.accountId ||
        account.code?.code ||
        playlists[0]?.id ||
        "unknown-account"
    ),
    displayName:
      account.displayName ||
      account.accountName ||
      playlists[0]?.name ||
      "Unnamed account",
    codeString,
    lunaKey,
    playlists,
    playlistCount,
    isLegacy: account.status === "legacy" || !account.accountId,
    isMultiPlaylist: playlistCount > 1,
    devicesCount: account.devicesCount ?? playlists[0]?.devicesCount ?? 0,
    expiresAt: lunaKey?.expiresAt || playlists[0]?.expiresAt || null,
    autoRenew: lunaKey?.autoRenew ?? playlists[0]?.autoRenew ?? false,
    redeemType: lunaKey?.redeemType || playlists[0]?.redeemType || "unknown",
    maxPlaylistsPerAccount: account.maxPlaylistsPerAccount ?? 3,
    canAddPlaylist: !readOnly && account.canAddPlaylist === true,
    addedDate:
      playlists[0]?.addedDate ||
      (account.playlists?.[0]?.addedDate
        ? new Date(account.playlists[0].addedDate).toISOString().split("T")[0]
        : null),
    lastAccessed: playlists.reduce((latest, playlist) => {
      if (!playlist.lastAccessed) return latest;
      if (!latest) return playlist.lastAccessed;

      return playlist.lastAccessed > latest ? playlist.lastAccessed : latest;
    }, null),
  };
};

export const buildPlaylistAccountGroups = (accounts = []) =>
  accounts.map(transformAccountGroupFromApi);

/**
 * Keeps only the account groups the signed-in user manages, so read-only
 * reseller-managed rows never become action targets or reseller metrics.
 *
 * @param {Array<object>} accountGroups
 * @returns {Array<object>}
 */
export const getOwnedPlaylistAccountGroups = (accountGroups = []) =>
  accountGroups.filter((account) => !isReadOnlyPlaylistEntry(account));

export const getPlaylistDashboardMetrics = (accountGroups = []) => ({
  accountCount: accountGroups.length,
  totalPlaylists: accountGroups.reduce(
    (sum, account) =>
      sum + (account.playlistCount || account.playlists?.length || 0),
    0
  ),
  totalDevices: accountGroups.reduce(
    (sum, account) => sum + (account.devicesCount || 0),
    0
  ),
});

const accountMatchesSearch = (account, query) => {
  const haystack = [
    account.displayName,
    account.codeString,
    account.accountName,
    ...(account.playlists || []).flatMap((playlist) => [
      playlist.name,
      playlist.playlistName,
      playlist.domain,
      playlist.username,
      playlist.code,
    ]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(query);
};

export const filterAndSortAccountGroups = (
  accountGroups,
  {
    searchQuery = "",
    selectedDomainFilter = null,
    kpiFilter = null,
    sortBy = "name",
    sortDirection = "asc",
  } = {}
) => {
  let filtered = [...accountGroups];
  const multiplier = sortDirection === "asc" ? 1 : -1;

  if (selectedDomainFilter) {
    filtered = filtered.filter((account) =>
      (account.playlists || []).some(
        (playlist) => playlist.domain === selectedDomainFilter
      )
    );
  }

  if (kpiFilter === "activeToday") {
    const today = new Date().toISOString().split("T")[0];

    filtered = filtered.filter((account) =>
      (account.playlists || []).some(
        (playlist) => playlist.lastAccessed === today
      )
    );
  } else if (kpiFilter === "expiringSoon") {
    const now = Date.now();
    const in14Days = now + 14 * 24 * 60 * 60 * 1000;

    filtered = filtered.filter((account) => {
      const { expiresAt } = account;

      if (!expiresAt) return false;
      const timestamp = new Date(expiresAt).getTime();

      return timestamp > now && timestamp < in14Days;
    });
  }

  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim();

    filtered = filtered.filter((account) =>
      accountMatchesSearch(account, query)
    );
  }

  filtered.sort((left, right) => {
    switch (sortBy) {
      case "playlistName":
        return (
          multiplier *
          String(left.playlists?.[0]?.playlistName || "").localeCompare(
            right.playlists?.[0]?.playlistName || ""
          )
        );
      case "domain":
        return (
          multiplier *
          String(left.playlists?.[0]?.domain || "").localeCompare(
            right.playlists?.[0]?.domain || ""
          )
        );
      case "username":
        return (
          multiplier *
          String(left.playlists?.[0]?.username || "").localeCompare(
            right.playlists?.[0]?.username || ""
          )
        );
      case "code":
        return (
          multiplier *
          String(left.codeString || "").localeCompare(right.codeString || "")
        );
      case "added":
        return (
          multiplier *
          (new Date(left.addedDate || 0).getTime() -
            new Date(right.addedDate || 0).getTime())
        );
      case "expires": {
        const leftDate = left.expiresAt
          ? new Date(left.expiresAt)
          : new Date("9999-12-31");
        const rightDate = right.expiresAt
          ? new Date(right.expiresAt)
          : new Date("9999-12-31");

        return multiplier * (leftDate.getTime() - rightDate.getTime());
      }
      case "lastAccessed": {
        const leftDate = left.lastAccessed
          ? new Date(left.lastAccessed).getTime()
          : 0;
        const rightDate = right.lastAccessed
          ? new Date(right.lastAccessed).getTime()
          : 0;

        return multiplier * (leftDate - rightDate);
      }
      case "devices":
        return (
          multiplier * ((left.devicesCount || 0) - (right.devicesCount || 0))
        );
      case "name":
      default:
        return (
          multiplier *
          String(left.displayName || "").localeCompare(right.displayName || "")
        );
    }
  });

  return filtered
    .map((account) => {
      if (!selectedDomainFilter && !searchQuery.trim()) {
        return account;
      }

      const playlists = (account.playlists || []).filter((playlist) => {
        if (selectedDomainFilter && playlist.domain !== selectedDomainFilter) {
          return false;
        }

        if (!searchQuery.trim()) return true;

        const query = searchQuery.toLowerCase().trim();

        return accountMatchesSearch(
          {
            ...account,
            playlists: [playlist],
          },
          query
        );
      });

      return {
        ...account,
        playlists,
        playlistCount: playlists.length,
        isMultiPlaylist: playlists.length > 1,
      };
    })
    .filter((account) => (account.playlists || []).length > 0);
};

export const getUniquePlaylistDomains = (accountGroups = []) => {
  const domains = new Set();

  for (const account of accountGroups) {
    for (const playlist of account.playlists || []) {
      if (playlist.domain) domains.add(playlist.domain);
    }
  }

  return [...domains].sort();
};

export const countPlaylistsForDomain = (accountGroups = [], domain) => {
  if (!domain) return 0;

  return accountGroups.reduce(
    (count, account) =>
      count +
      (account.playlists || []).filter((playlist) => playlist.domain === domain)
        .length,
    0
  );
};

export const getAccountGroupsExpiringSoon = (accountGroups = [], withinMs) => {
  const now = Date.now();
  const cutoff = now + withinMs;

  return accountGroups
    .filter((account) => {
      if (!account.expiresAt) return false;
      const timestamp = new Date(account.expiresAt).getTime();

      return timestamp > now && timestamp < cutoff;
    })
    .sort(
      (left, right) =>
        new Date(left.expiresAt).getTime() - new Date(right.expiresAt).getTime()
    );
};

export const getAccountGroupsActiveToday = (accountGroups = []) => {
  const today = new Date().toISOString().split("T")[0];

  return accountGroups.filter((account) =>
    (account.playlists || []).some(
      (playlist) => playlist.lastAccessed === today
    )
  );
};
